import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useState } from "react";
import {
  Paper,
  Title,
  TextInput,
  Button,
  Stack,
  Box,
  Group,
  Divider,
  Alert,
  Grid,
} from "@mantine/core";
import { IconCheck, IconX } from "@tabler/icons-react";
import { PageLoader } from "@/components/common";
import { settingsApi, geocodeApi } from "@/api/client";
import type { Settings } from "@/api/types";

export const Route = createFileRoute("/_authenticated/settings")({
  component: SettingsPage,
});

function SettingsPage() {
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const queryClient = useQueryClient();

  const { data: settings, isLoading } = useQuery({
    queryKey: ["settings"],
    queryFn: settingsApi.get,
  });

  const { register, handleSubmit, setValue, watch } = useForm<Settings>({
    values: settings,
  });

  const updateMutation = useMutation({
    mutationFn: settingsApi.update,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settings"] });
      setMessage({ type: "success", text: "Settings saved successfully" });
    },
    onError: () => {
      setMessage({ type: "error", text: "Failed to save settings" });
    },
  });

  const geocodeMutation = useMutation({
    mutationFn: (address: string) => geocodeApi.geocode(address),
    onSuccess: (data) => {
      if ("latitude" in data) {
        setValue("office_latitude", data.latitude.toString());
        setValue("office_longitude", data.longitude.toString());
        setMessage({ type: "success", text: "Address geocoded successfully" });
      } else {
        setMessage({ type: "error", text: data.error });
      }
    },
    onError: () => {
      setMessage({ type: "error", text: "Failed to geocode address" });
    },
  });

  const officeAddress = watch("office_address");

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <Box maw={700} mx="auto">
      <Stack gap="md">
        <Title order={1}>Settings</Title>

        {message && (
          <Alert
            icon={message.type === "success" ? <IconCheck size={16} /> : <IconX size={16} />}
            color={message.type === "success" ? "green" : "red"}
            variant="light"
            onClose={() => setMessage(null)}
            withCloseButton
          >
            {message.text}
          </Alert>
        )}

        <Paper p="md" radius="md" withBorder style={{ borderColor: "#0f3460" }}>
          <form onSubmit={handleSubmit((data) => updateMutation.mutate(data))}>
            <Stack gap="md">
              <Title order={3}>Office Location</Title>

              <Group align="flex-end">
                <TextInput
                  label="Office Address"
                  {...register("office_address")}
                  placeholder="e.g., Kungsgatan 1, Stockholm"
                  style={{ flex: 1 }}
                />
                <Button
                  variant="filled"
                  color="#0f3460"
                  onClick={() => officeAddress && geocodeMutation.mutate(officeAddress)}
                  loading={geocodeMutation.isPending}
                >
                  Geocode
                </Button>
              </Group>

              <Grid>
                <Grid.Col span={6}>
                  <TextInput
                    label="Latitude"
                    {...register("office_latitude")}
                    placeholder="e.g., 59.3293"
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <TextInput
                    label="Longitude"
                    {...register("office_longitude")}
                    placeholder="e.g., 18.0686"
                  />
                </Grid.Col>
              </Grid>

              <Divider my="sm" color="#0f3460" />

              <Title order={3}>API Keys</Title>
              <TextInput
                label="Trafiklab API Key"
                {...register("trafiklab_api_key")}
                placeholder="Your Trafiklab API key for transit times"
              />

              <Button type="submit" loading={updateMutation.isPending}>
                Save Settings
              </Button>
            </Stack>
          </form>
        </Paper>
      </Stack>
    </Box>
  );
}
